<template>
    <Dialog :visible="visible" modal dismissableMask :closable="false" :draggable="false"
        :style="{ width: '55rem', maxWidth: '95vw' }" contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }" @update:visible="v => !v && handleClose()">
        <!-- HEADER -->
        <template #header>
            <div class="flex items-center gap-4 w-full p-3 rounded-t-lg bg-gradient-to-r from-green-50 to-emerald-50">
                <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-bell text-white text-xl" />
                </div>

                <div class="flex-1 min-w-0">
                    <h4 class="text-xl font-bold">
                        {{ title }}
                    </h4>
                </div>

                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    @click="handleClose()" />
            </div>
        </template>

        <!-- BODY -->
        <div class="flex flex-col" style="min-height: 400px; max-height: 600px;">
            <!-- Loading State -->
            <div v-if="loading" class="flex flex-col justify-center items-center flex-1 py-12">
                <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
                <p class="mt-4">{{ t('common.loading') }}</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="Object.keys(notifications).length === 0"
                class="flex flex-col items-center justify-center flex-1 py-12 px-6">
                <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-surface-100 mb-4">
                    <i class="fa-regular fa-bell text-4xl" />
                </div>
                <h4 class="font-semibold text-surface-900 text-lg mb-2">{{ t('userGroups.noNotificationsConfigured') }}
                </h4>
            </div>

            <!-- Notifications List -->
            <div v-else class="flex-1 overflow-y-auto p-3">
                <!-- Notification Cards -->
                <div class="space-y-4">
                    <div v-for="notification in Object.values(notifications)" :key="notification.resourceType.id"
                        class="border-2 rounded-xl p-3">

                        <!-- HEADER -->
                        <div class="flex items-start justify-between mb-2">
                            <div class="flex items-start gap-3 flex-1">
                                <div class="flex-1">
                                    <h5 class="font-semibold">
                                        {{ notification.resourceType.name }}
                                    </h5>
                                </div>
                            </div>
                            <Tag :value="notification.enabled ? t('common.enabled') : t('common.disabled')"
                                :severity="notification.enabled ? 'success' : 'secondary'" class="font-medium" />
                        </div>

                        <!-- FLAGS -->
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-surface-200">
                            <!-- Create -->
                            <div
                                :class="['flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all', notification.notifyOnCreate ? 'bg-green-50 border-green-500 text-green-700' : 'bg-surface-50 border-surface-300']">
                                <i class="fa-solid fa-plus-circle text-lg mb-1" />
                                <span class="font-medium text-center">{{ t('userGroups.onCreate') }}</span>
                            </div>

                            <!-- Update -->
                            <div
                                :class="['flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all', notification.notifyOnUpdate ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-surface-50 border-surface-300']">
                                <i class="fa-regular fa-pen-to-square text-lg mb-1" />
                                <span class="font-medium text-center">{{ t('userGroups.onUpdate') }}</span>
                            </div>

                            <!-- Delete -->
                            <div
                                :class="['flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all', notification.notifyOnDelete ? 'bg-red-50 border-red-500 text-red-700' : 'bg-surface-50 border-surface-300']">
                                <i class="fa-regular fa-trash-can text-lg mb-1" />
                                <span class="font-medium text-center">{{ t('userGroups.onDelete') }}</span>
                            </div>

                            <!-- Unconfirmed -->
                            <div
                                :class="['flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all', notification.notifyOnUnconfirmed ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-surface-50 border-surface-300']">
                                <i class="fa-solid fa-triangle-exclamation text-lg mb-1" />
                                <span class="font-medium text-center">{{ t('userGroups.onUnconfirmed') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- FOOTER -->
        <template #footer>
            <div class="flex justify-end gap-3 p-3 border-t bg-gray-50 w-full">
                <Button :label="t('common.close')" icon="fa-solid fa-xmark" severity="secondary" outlined
                    @click="handleClose()" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { UserGroupNotification } from '@/lib/types/userGroup';
import { useI18n } from 'vue-i18n';

interface Props {
    visible: boolean;
    title: string;
    notifications: Record<string, UserGroupNotification>;
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    loading: false
});

const emit = defineEmits(['close']);

const { t } = useI18n();

function handleClose() {
    emit('close');
}
</script>
